import { v4 as uuid4 } from 'uuid';
import { useState, useEffect, useRef } from 'react';

/**
 * Returns the default language for portal integration.
 * If the current UI language is in supported languages, use it.
 * Otherwise, use the first supported language.
 */
export const getDefaultPortalLanguage = (supportedLanguages: string[] | undefined): string => {
  const currentLang = window.appState.lang;
  if (supportedLanguages?.includes(currentLang)) {
    return currentLang;
  }
  return supportedLanguages?.[0] || currentLang;
};

const portalCodeTemplate = `<div
  id="{portalId}"
  data-language="{language}"
  data-company-id="{companyId}"
  data-base-url="{baseUrl}"{dataFormIds}
></div>
<script type="text/javascript" id="retino-embedded-form-script">
  (function(r,e,t,i,n,o){n=e.createElement(t);n.src=i;n.async=true;o=e.getElementsByTagName(t)[0];o.parentNode.insertBefore(n,o)})(window,document,'script','{staticUrl}embeddedFormInjector.js')
</script>`;

type UsePortalCodeOptions = {
  rootId?: string;
  formIds?: string[];
};

export const usePortalCode = (companyId: UUIDField, language: string, { rootId, formIds }: UsePortalCodeOptions) => {
  const portalRootId = useRef(rootId || `retino-portal-${uuid4().slice(0, 4)}`);
  const [scriptCode, setScriptCode] = useState('');

  useEffect(() => {
    let dataFormIds = '';
    if (formIds?.length !== 0) {
      dataFormIds = `\n  data-form-ids="${formIds?.join(';') || ''}"`;
    }
    setScriptCode(
      portalCodeTemplate
        .replace('{portalId}', portalRootId.current)
        .replace('{companyId}', companyId)
        .replace('{language}', language)
        .replace('{baseUrl}', window.location.origin)
        .replace('{dataFormIds}', dataFormIds)
        .replace('{staticUrl}', window.appState.baseStaticUrl),
    );
  }, [companyId, formIds, language]);

  return scriptCode;
};

const shippingPortalCodeTemplate = `
<div
  id="{portalId}"
  data-company-id="{companyId}"
  data-language="{language}"
  data-base-url="{baseUrl}"
/></div>
<script type="text/javascript" id="retino-shipping-portal-script">
  (function(r,e,t,i,n,o){n=e.createElement(t);n.src=i;n.async=true;o=e.getElementsByTagName(t)[0];o.parentNode.insertBefore(n,o)})(window,document,'script','{staticUrl}shipping-portal/injector.js')
</script>`;

export const useShippingPortalCode = (companyId: UUIDField, language: string, { rootId }: UsePortalCodeOptions) => {
  const portalRootId = 'shipping-portal';
  const [scriptCode, setScriptCode] = useState('');

  useEffect(() => {
    setScriptCode(
      shippingPortalCodeTemplate
        .replace('{portalId}', portalRootId)
        .replace('{companyId}', companyId)
        .replace('{language}', language)
        .replace('{baseUrl}', window.location.origin)
        .replace('{staticUrl}', window.appState.baseStaticUrl),
    );
  }, [companyId, language]);

  return scriptCode;
};

const trackNTraceCodeTemplate = `
<div
  id="retino-track-n-trace"
  data-page-id="{pageId}"
  data-language="{language}"
  data-base-url="{baseUrl}"
></div>
<script type="text/javascript" id="retino-track-n-trace-script">
  (function(r,e,t,i,n,o){n=e.createElement(t);n.src=i;n.async=true;o=e.getElementsByTagName(t)[0];o.parentNode.insertBefore(n,o)})(window,document,'script','{staticUrl}trackntraceInjector.js')
</script>`;

export const useTrackNTraceCode = (pageId: string, language: string) => {
  const [scriptCode, setScriptCode] = useState('');

  useEffect(() => {
    setScriptCode(
      trackNTraceCodeTemplate
        .replace('{pageId}', pageId)
        .replace('{language}', language)
        .replace('{baseUrl}', window.location.origin)
        .replace('{staticUrl}', window.appState.baseStaticUrl),
    );
  }, [pageId, language]);

  return scriptCode;
};
